import { LatestActivityObject } from "./playbook";
import { Subscription } from "./summary";

export enum AccountStatus {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  DISABLED = "DISABLED",
}

export enum AccountType {
  DIRECT = "Direct",
  ONLINE = "Online",
  "SELL-THROUGH" = "Sell-Through",
}

export enum Channel {
  "ONLINE-WEBEX 11" = "Online-Webex 11",
  "ONLINE-MC ONLINE" = "Online-MC Online",
  BLIS = "BLIS",
  "ONLINE-BABY BEAR" = "Online-Baby Bear",
  "ONLINE-WEBEX CLASSIC" = "Online-Webex Classic",
  "ONLINE-WEBEX TEAMS ONLINE" = "Online-Webex Teams Online",
  "ANNUITY-DIRECT" = "Annuity Direct",
  "ANNUITY-NON-DIRECT" = "Annuity Non-Direct",
}

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roleName: string;
  phoneNumber: string | null;
  jobTitle: string | null;
  department: string | null;
  distributed: boolean;
  bpp: number;
  bppDetails: BppDetail[];
}

export interface Tag {
  id: string;
  name: string;
}

export interface Competitor {
  accountKey: string;
  active: boolean;
  comments: string;
  competitorName: string;
  discoveryBy: string;
  discoveryDate: string;
  discoveryMethod: string;
  siteName: string | null;
}

export type CompetitorKey = "ZOOM" | "MICROSOFT" | "LOGMEIN" | "BLUEJEANS";

export type CompetitorDiscoveries = Record<CompetitorKey, Competitor[]>;

export type AlertDismissal = "AROFFPC" | "";

export interface AlertOverview {
  usageCount: number;
  supportCount: number;
  churnRiskCount: number;
  healthCount: number;
  aemCount: number;
}

export interface ExecutiveOverview {
  topExecAccounts: number;
  myAccounts: number;
  teamAccounts: number;
  webexSuite: number;
  renewalAlert: number;
  activeRadioAlerts: number;
  supportAlerts: number;
  riskChangedAlerts: number;
  epRequiredAMER: number;
  epRequiredEMEA: number;
  epRequiredAPJC: number;
}

export interface Account {
  accountKey: string;
  accountId: string;
  accountSK: number;
  attributes: {
    latestAlerts: { alertSubType: string; createdDate: string; name: string };
    latestActivity: LatestActivityObject;
    lifecycleObjectives: string[] | null;
  };
  organizationUnitId: string;
  accountName: string;
  companyName: string;
  accountType: AccountType;
  sourceSystemCode: string;
  channel: Channel;
  accountStatus: AccountStatus;
  country?: {
    code: string;
    name: string;
    region: string;
  };
  city: string;
  partnerAccountName: string;
  partnerGroup: string;
  lifecyclePartnerFlag: boolean;
  salesOwner: string;
  salesCoverage: string;
  subSalesCoverage: string;
  contractTheatre: string;
  salesLevel1: string;
  salesLevel2: string;
  salesLevel3: string;
  salesLevel4: string;
  salesLevel5: string;
  salesLevel6: string;
  renewalDate: string;
  renewalQuarter: string;
  accountAgeMonths: number;
  daysToRenewal?: number;
  swssAccountTypeIndicator: null | string;
  verticalIndustryName: string;
  zeroRenewalTerm: boolean;
  sites: Site[];
  invoiceMrr: number;
  invoiceArr: number;
  mrr: number;
  arr: number;
  ciscoCsm: {
    cecId: string;
    name: string;
    email: string;
    managerId: string;
    managerName: string;
  } | null;
  ciscoManagerName: string | null;
  partnerCsm: {
    cecId: string;
    name: string;
    email: string;
    managerId: string;
    managerName: string;
  } | null;
  partnerManagerName: string | null;
  psm: {
    cecId: string;
    name: string;
    email: string;
    managerId: string;
    managerName: string;
  } | null;
  dsmName: string | null;
  escalationEnggName: string | null;
  ccseName: string | null;
  ccseManagerName: string | null;
  new: boolean;
  alertReasonWebex: string;
  alertReasonSpark: string;
  utilizationAlert: boolean;
  anchorProducts: string[];
  webexSuiteFlag: boolean;
  webexOverallAlertStatus: string;
  sparkOverallAlertStatus: string;
  priorityIndicatorRank: number;
  contacts: Contact[];
  expandPlanCreatedDate: string;
  personalTags: Tag[];
  managementTags: Tag[];
  competitorDiscoveries: CompetitorDiscoveries;
  partnerExpandPlanCreatedDate: string | null;
  favorite: boolean;
  arOffPCCount: number;
  subscriptionCount: number;
  alertDismissals: AlertDismissal[];
  confidential: boolean;
  knowledgeWorkCount: number;
  state: string | null;
  healthScore: number | null;
  alertGroup: string | null;
  conflictColor: string;
  invoiceArrCalling: number | null;
  /**
   * @deprecated
   */
  invoiceArrMeetingsMessaging: number | null;
  invoiceMrrCalling: number | null;
  /**
   * @deprecated
   */
  invoiceMrrMeetingsMessaging: number | null;
  invoiceArrCollab: number | null;
  invoiceArrNonCollab: number | null;
  invoiceArrOnPrem: number | null;
  invoiceArrCloud: number | null;
  invoiceArrTotal: number | null;
  invoiceArrOther: number | null;
  invoiceArrNoCPass: number | null;
  expandPlanRiskAssessment: string | null;
  partnerExpandPlanRiskAssessment: string | null;
  customerContacts: Contact[];
  teamContacts: Contact[];
  pendingMrr: number | null;
  pendingInvoiceMrr: number | null;
  pendingArr: number | null;
  pendingInvoiceArr: number | null;
  pendingCount: number;
  mrrTrend: "up" | "flat" | "down";
  invoiceMrrTrend: "up" | "flat" | "down";
  collabMapDate: string;
  conciergeStatus: string;
  conciergeFunded: string;
  conciergeEffectiveFrom: string;
  conciergeEffectiveTo: string;
  callingPsmId: string | null;
  callingPsmName: string | null;
  callingPsmManagerName: string | null;
  callingEpsmId: string | null;
  callingEpsmName: string | null;
  callingEpsmManagerName: string | null;
  invoiceArrCallingCloud: number | null;
  invoiceArrCallingOnPrem: number | null;
  invoiceArrMeetingMsgCloud: number | null;
  invoiceArrMeetingMsgOnPrem: number | null;
  invoiceArrContactCenterCloud: number | null;
  invoiceArrContactCenterOnPrem: number | null;
  invoiceArrDeviceCloud: number | null;
  invoiceArrDeviceOnPrem: number | null;
  invoiceArrNonCollabSecurity: number | null;
  invoiceArrNonCollabService: number | null;
  invoiceArrNonCollabOther: number | null;
  invoiceArrSuite: number | null;
  invoiceArrSuiteCallingCloud: number | null;
  invoiceArrSuiteCallingOnPrem: number | null;
  invoiceArrSuiteCustom: number | null;
  invoiceArrSuiteMeetingCloud: number | null;
  invoiceArrSuiteMeetingOnPrem: number | null;
  invoiceArrSuiteOther: number | null;
  contractId: string | null;
  subscriptionRefId: string | null;
  webOrderId: string | null;
  email: string | null;
  invoiceArrCPassCloud: number | null;
  alertsOverview?: AlertOverview;
  invoiceArrOrgTotal: number | null;
  organizationStatus: string | null;
  isOwner: boolean;
  activityStatus: string | null;
  bpp: number;
  bppProducts: BppCountDetail[];
  callingBooked: number;
  callingMau: number;
  callingProvisioned: number;
  ciscoCsmName: string | null;
  partnerCsmName: string | null;
  ciOrgFoundationalStatus: string;
  ciOrgAdvanceStatus: string;
  ciOrgNum: number;
  ciOrgFoundationalNum: number;
  ciOrgAdvanceNum: number;
  productServices: string[];
  cavBuId: number | null;
  cavBuName: string | null;
  cavId: number | null;
  cavName: string | null;
  cgemFlag: string;
  subscriptionRefIds: string | null;
  subscriptionCodes: string | null;
  videoDeviceAmount6M: number;
  videoDeviceQty6M: number;
  videoDeviceAmount12M: number;
  videoDeviceQty12M: number;
  videoDeviceAmount24M: number;
  videoDeviceQty24M: number;
  videoDeviceQty3Y: number;
  videoDeviceAmount3Y: number;
  deviceAmount6M: number;
  deviceQty6M: number;
  deviceAmount12M: number;
  deviceQty12M: number;
  deviceAmount24M: number;
  deviceQty24M: number;
  deviceQty3Y: number;
  deviceAmount3Y: number;
  adoptionCloud6M: number;
  adoptionCloud12M: number;
  adoptionCloud24M: number;
  adoptionCloud3Y: number;
  adoptionEdge6M: number;
  adoptionEdge12M: number;
  adoptionEdge24M: number;
  adoptionEdge3Y: number;
  adoptionUnknown6M: number;
  adoptionUnknown12M: number;
  adoptionUnknown24M: number;
  adoptionUnknown3Y: number;
  videoDeviceCloudAmount6M: number;
  videoDeviceCloudAmount12M: number;
  videoDeviceCloudAmount24M: number;
  videoDeviceCloudAmount3Y: number;
  aemExpReason: string;
  aemExpBy: string;
  aemExpDate: string;
  wemMeeting: string;
  wemMeetingExpReason: string;
  wemMeetingExpBy: string;
  wemMeetingExpDate: string;
  wemCallingCloud: string;
  wemCallingCloudExpReason: string;
  wemCallingCloudExpBy: string;
  wemCallingCloudExpDate: string;
  wemCallingOnPrem: string;
  wemCallingOnPremExpReason: string;
  wemCallingOnPremExpBy: string;
  wemCallingOnPremExpDate: string;
  wemContactCenter: string;
  wemContactCenterExpReason: string;
  wemContactCenterExpBy: string;
  wemContactCenterExpDate: string;
  wemVideoDevice: string;
  wemVideoDeviceExpReason: string;
  wemVideoDeviceExpBy: string;
  wemVideoDeviceExpDate: string;
  wemSuite: string;
  wemSuiteExpReason: string;
  wemSuiteExpBy: string;
  wemSuiteExpDate: string;
  coverageStatus: string;
  wholesaleFlag: boolean;
  bannerFlag: number;
  suiteAnchor: string[] | null;
  successPlan: any;
  evCsmId: string;
  evCsmName: string;
  evCsmManagerName: string;
  invoiceArrCloudWXCC: number | null;
  invoiceArrCloudWXCCAddon: number | null;
  invoiceArrCloudWXCCAddonUnsupported: number | null;
  wellPlanIndFlag: boolean;
  ccseActivityLvl: string | null;
  [key: string]: any;
  summarizedData: string | null;
  summarizedDate: string | null;
  savId: string | null;
  savName: string | null;
  wholesaleCustomerFlag: boolean;
  wholesalePartnerId: string | null;
  wholesalePartnerName: string | null;
  invoiceArrCloudCviDevice: number | null;
  subscriptions: Subscription[];
  defaultLanguage: string | null;
}

export interface AccountWithPaging {
  data: Account[];
  "total-elements": number;
  "total-pages": number;
}

export interface Counts {
  myAccounts: number;
  teamAccounts: number;
  newAccounts: number;
  favoriteAccounts: number;
  zeroRenewalAccounts: number;
  trueForwardAlerts: number;
  pendingOrders: number;
  overageAlerts: number;
  accountsWithResource: number;
  top500AccountsWithoutResource: number;
  arOffPCAlerts: number;
  myAccountsInTop150: number;
  myAccountsInTop500: number;
  webexSuiteAccounts: number;
  epRequiredAMER: number;
  epRequiredEMEA: number;
  epRequiredAPJC: number;
  assessRequiredEMEA: number;
  assessRequiredAMER: number;
  assessRequiredAPJC: number;
  highTouch: number;
  highTouchRenewals: number;
  bigDeals: number;
  deviceSeeds: number;
  healthTotal: number;
  healthSignificant: number;
  healthModerate: number;
  healthMinimal: number;
}

export interface BannerDataType {
  createdDate: string;
  createdBy: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  id: string;
  page: string;
  message: string;
  bannerType: "info" | "success" | "warning" | "error";
  expirationDate: string;
}

export interface Site {
  accountKey: string;
  clientVersion: string;
  clusterCode: string | null;
  clusterDataCenter: string;
  clusterName: string;
  hgsSiteId: number;
  primarySite: boolean;
  siteKey: string;
  siteName: string | null;
  siteUrl: string;
  status: string;
  lockDownFlag: string;
  healthScore: number;
  subscriptionKey: string;
  active: boolean;
  ciOrgId: string | null;
  meetingNPSScore: number | null;
  wsmpFlag: string;
  wsmpDate: string;
  desktopAppVersion: string;
  aiEnabledFlag: string;
  aiEnabledDate: string;
  wswpFlag: string;
  wswpDate: string;
}

export interface SiteInSiteSummary {
  primarySite: boolean;
  deploymentModel: string;
  hasUsage: boolean;
  latestHgsSiteId: number;
  product: string;
  siteUrl: string;
  totPeopleMins36M: number;
  clusterCode: string | null;
  clusterName: string | null;
  hgsSiteId?: number;
  mappingValid: boolean;
  fedRAMPFlag?: string;
}

export interface SiteSummary {
  accountKey: string;
  sites: SiteInSiteSummary[];
  sourceSystemCode: string;
}

export interface ExpandPlanVersionDataType {
  createdDate: string;
  createdBy: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  id: string;
  versionName: string;
  accountKey: string;
  status: number;
}

export interface Cluster {
  name: string;
  code: string;
  dataCenter: string;
}

export interface FavoriteEventDetail {
  type: "ADD" | "REMOVE";
  payload: string[];
}

export interface InvoiceChangeDetail {
  accountKey: string;
  changedDate: string;
  invoiceMrr: number | null;
  invoiceArr: number | null;
  mrr: number | null;
  arr: number | null;
  reasonForChange: string;
  estimated: boolean;
}

export interface CCSEMetadata {
  ccseId: string;
  ccseName: string;
}

export interface ConciergeHistory {
  id: string;
  accountKey: string;
  status: string;
  funded: string;
  effectiveFrom: string;
  effectiveTo: string;
  createdDate: string;
  createdBy: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  creatorName: string;
  modifierName: string;
}

export interface CallingPsmMetadata {
  cecId: string;
  name: string;
  active: boolean;
  managerId: string;
  managerName: string;
}

export interface SmartSearchOverview {
  arOffPCCount: number;
  arr: number;
  confidential: boolean;
  invoiceArr: number;
  invoiceArrCPassCloud: number;
  invoiceArrCallingCloud: number;
  invoiceArrCallingOnPrem: number;
  invoiceArrCloud: number;
  invoiceArrCollab: number;
  invoiceArrContactCenterCloud: number;
  invoiceArrContactCenterOnPrem: number;
  invoiceArrDeviceCloud: number;
  invoiceArrDeviceOnPrem: number;
  invoiceArrMeetingMsgCloud: number;
  invoiceArrMeetingMsgOnPrem: number;
  invoiceArrNonCollab: number;
  invoiceArrNonCollabOther: number;
  invoiceArrNonCollabScary: number;
  invoiceArrNonCollabSecurity: number;
  invoiceArrNonCollabService: number;
  invoiceArrOnPrem: number;
  invoiceArrOnPremAndCloud: number;
  invoiceArrOnPremAndCloudAndOther: number;
  invoiceArrOther: number;
  invoiceArrTotal: number;
  invoiceMrr: number;
  lifecyclePartnerFlag: boolean;
  mrr: number;
  pendingArr: number;
  pendingCount: number;
  pendingInvoiceArr: number;
  pendingInvoiceMrr: number;
  pendingMrr: number;
  status: "Active" | "Inactive";
  subscriptionCount: number;
}

export interface BppDetail {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface BppCountDetail {
  program: string;
  participation: number;
}

export interface BppData {
  totalParticipation: number;
  details: BppCountDetail[];
}

export interface CIOrgDetail {
  ciOrgId: string;
  ciOrgName: string;
  foundationalStatus: string;
  verifiedDomain: boolean;
  chExternalUser: number | string;
  claimedDomain: boolean;
  autoAssignTemplate: boolean;
  advanceStatus: string;
  ssoEnabledCH: boolean;
  directoryService: boolean;
  hybridCalendar: boolean;
}

export interface AnchorHistory {
  anchorProducts: string[];
  createdBy: string;
  creatorName: string;
  creatorRole: string;
  createdDate: string;
}

export interface CoverageStatusHistory {
  createdDate: string;
  createdBy: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
  value: string;
  creatorName: string;
  creatorRole: string;
  reason: string;
}
